/**
 * Admin Sale Picker — lists native auctions + imported sales side by side.
 *
 * Route: /admin/sales
 *
 * Imports show with their source (LiveAuctioneers, AuctionFlex, …) and
 * native auctions show with their status (live, scheduled, ended, draft).
 * Clicking a sale navigates to the relevant admin view (warehouse picker
 * for ended sales, the sale console for live ones).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { sortSalesForListing } from '../utils/currentSale';

interface SaleRow {
  id: string;
  title: string;
  source: string;
  isImport: boolean;
  totalLots: number;
  date: string;
  status: string;
}

const STATUS_ICON: Record<string, string> = {
  live: '🟢',
  scheduled: '🕒',
  ended: '✅',
  draft: '📝',
  imported: '📥',
};

const SalePicker: React.FC = () => {
  const [sales, setSales] = useState<SaleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'native' | 'imported'>('all');

  useEffect(() => {
    loadSales();
  }, []);

  async function loadSales() {
    setLoading(true);
    try {
      // Native auctions live in /auctions. Imported sales we mirror there
      // with importSource set, so we read this collection once and partition
      // by importSource.
      //
      // 🔴 UNORDERED on purpose. This used to be orderBy('createdAt','desc'),
      // which does not sort documents missing the field to the end — Firestore
      // OMITS them. On the one page whose entire job is "show me every sale",
      // any auction without a createdAt was simply absent from the list, with
      // nothing to indicate a row was missing. Ordering now happens in memory,
      // on the real end of the sale, and every document survives it.
      const auctionsSnap = await getDocs(collection(db, 'auctions'));
      const docs = auctionsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Auction[];

      // One shared listing order, also used by Dashboard and AuctionList, so all
      // three seller-facing lists agree. Newest real end first, drafts included.
      const ordered = sortSalesForListing(docs);

      const rows: SaleRow[] = ordered.map((data: any) => {
        const isImport = !!data.importSource && String(data.id).startsWith('import-');
        return {
          id: data.id,
          title: data.title || data.id,
          source: isImport ? data.importSource : 'native',
          isImport,
          totalLots: data.totalLots ?? data.lots?.length ?? 0,
          date: formatDate(data.startDate ?? data.createdAt),
          status: isImport ? 'imported' : data.status || 'unknown',
        };
      });

      setSales(rows);
    } catch (e) {
      console.error('Failed to load sales:', e);
    } finally {
      setLoading(false);
    }
  }

  const filtered = sales.filter((s) =>
    filter === 'all'
      ? true
      : filter === 'imported'
      ? s.isImport
      : !s.isImport,
  );

  const nativeCount = sales.filter((s) => !s.isImport).length;
  const importCount = sales.filter((s) => s.isImport).length;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold">All Sales</h1>
          <p className="text-gray-600 mt-1">
            Native WhiteLabelBuddy auctions plus imported orders from any platform.
          </p>
        </div>
        <Link
          to="/admin/import-orders"
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
        >
          + Import New Sale
        </Link>
      </header>

      <div className="bg-white rounded-lg shadow p-4 flex items-center gap-2">
        {(['all', 'native', 'imported'] as const).map((opt) => (
          <button
            key={opt}
            onClick={() => setFilter(opt)}
            className={`px-3 py-1.5 rounded text-sm font-semibold ${
              filter === opt
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {opt === 'all'
              ? `All (${sales.length})`
              : opt === 'native'
              ? `Native (${nativeCount})`
              : `Imported (${importCount})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          No sales match this filter.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Sale
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Source
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                  Lots
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-2xl">
                    {STATUS_ICON[s.status] || '❓'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{s.title}</div>
                    <div className="text-xs font-mono text-gray-400">{s.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        s.isImport
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {s.source}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">{s.totalLots}</td>
                  <td className="px-4 py-3">{s.date}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <Link
                      to={`/auctions/${s.id}/invoices`}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Invoices
                    </Link>
                    {/*
                      This link was dead: nothing in the app read `?auction=`, so
                      clicking a specific sale here silently landed you on
                      whatever the Picker's default happened to be. It is honoured
                      now — useSaleSelection reads the param at the top of its
                      precedence order, above the stored pin and the resolver.
                      Shipping gets the same treatment so a sale can be opened
                      directly on either fulfilment page.
                    */}
                    <Link
                      to={`/picker?auction=${s.id}`}
                      className="text-green-600 hover:underline text-sm"
                    >
                      Picker
                    </Link>
                    <Link
                      to={`/shipping?auction=${s.id}`}
                      className="text-orange-600 hover:underline text-sm"
                    >
                      Shipping
                    </Link>
                    {!s.isImport && (
                      <Link
                        to={`/admin/sale/${s.id}`}
                        className="text-purple-600 hover:underline text-sm"
                      >
                        Console
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

function formatDate(ts: any): string {
  if (!ts) return '—';
  if (ts.toDate) {
    return ts.toDate().toLocaleDateString();
  }
  if (typeof ts === 'string') {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? ts : d.toLocaleDateString();
  }
  if (ts instanceof Date) return ts.toLocaleDateString();
  return '—';
}

export default SalePicker;
